import React, { ChangeEvent } from "react";
import { T } from "services/lang/LanguageService";

import groupedUnits from "components/basic/dropdowns/dataset-unit-selector/units";
import {
  HighlightRange,
  IProductTemplateAttribute,
  Validation,
} from "components/dashboard/product-template-table/utils/interfaces";
import { Chips } from "primereact/chips";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { CellType, ColumnAggregationType } from "utils/enums";
import { InputSwitch } from "primereact/inputswitch";
import OutputHighlight from "./components/OutputHighlight";
import ValidationOptions from "./components/ValidationOptions";

type LongAndDoubleTypesProps = {
  attribute: IProductTemplateAttribute;
  onAttrChangeInLongAndDouble: React.Dispatch<React.SetStateAction<IProductTemplateAttribute>>;
};

const LongAndDoubleTypes: React.FC<LongAndDoubleTypesProps> = ({
  attribute,
  onAttrChangeInLongAndDouble,
}) => {
  const bgHighlightList = [...(attribute.bgHighlightRanges ?? [])];

  const updateAttributeChange = (newBgList: HighlightRange[]) => {
    onAttrChangeInLongAndDouble(prev => ({
      ...prev,
      bgHighlightRanges: newBgList,
    }));
  };

  const onFieldChange = (
    e: DropdownChangeEvent | InputNumberValueChangeEvent | ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    if (value !== 0 && !value) {
      onAttrChangeInLongAndDouble(prev => {
        const { [name as keyof IProductTemplateAttribute]: remove, ...rest } = prev;
        return rest as IProductTemplateAttribute;
      });
    }
    onAttrChangeInLongAndDouble(prev => ({ ...prev, [name]: value }));
  };

  const handleValidationChange = (validators: Validation[]) => {
    onAttrChangeInLongAndDouble(prev => ({
      ...prev,
      validationOptions: {
        ...prev.validationOptions,
        validators,
      },
    }));
  };

  return (
    <>
      <div className="p-field p-col-2">
        <label>{T("word.unit")}</label>
        <Dropdown
          value={attribute.unit}
          onChange={onFieldChange}
          name="unit"
          className="p-inputtext-sm"
          options={groupedUnits}
          optionLabel="label"
          optionGroupLabel="label"
          optionGroupChildren="items"
          placeholder={T("action.select-a-unit")}
          showClear
          editable
        />
      </div>
      {attribute.type === CellType.DOUBLE && (
        <div className=" p-field p-col-2">
          <label>{T("config.decimal-places")}</label>
          <InputNumber
            placeholder={T("message.number-of-decimal-places-to-show")}
            value={attribute.decimalPlaces}
            onValueChange={onFieldChange}
            name="decimalPlaces"
            className="p-inputtext-sm"
          />
        </div>
      )}

      <div className="p-field p-col-2">
        <label>{T("label.aggregation-type")}</label>
        <Dropdown
          options={Object.values(ColumnAggregationType)}
          value={attribute.aggregationType}
          onChange={onFieldChange}
          placeholder={T("action.select-an-aggregation-type")}
          showClear
          name="aggregationType"
          className="p-inputtext-sm"
        />
      </div>
      <div className="p-field p-col-6">
        <label>{T("label.evaluation-expression")}</label>
        <InputText
          placeholder="e.g. $some_attr1 + $some_attr3 * $some_attr_2"
          value={attribute.evaluationExpression ?? ""}
          onChange={onFieldChange}
          name="evaluationExpression"
          className="p-inputtext-sm"
        />
      </div>
      <div className="p-field p-col-5">
        <label>{T("label.selection-values")}</label>
        <Chips
          value={attribute.selectionValues ?? []}
          onChange={onFieldChange}
          name="selectionValues"
          separator=","
          keyfilter="num"
        />
      </div>
      <div className="p-field p-col-3">
        <label>{T("label.default-value")}</label>
        <Dropdown
          value={attribute.defaultValue}
          options={attribute.selectionValues ?? []}
          onChange={onFieldChange}
          name="defaultValue"
          className="p-inputtext-sm"
          editable
          showClear
        />
      </div>
      <div className="p-col-4 flex align-self-center justify-content-center gap-3">
        <label>{T("word.format-number")}</label>
        <InputSwitch
          checked={attribute.shortenText ?? true}
          onChange={onFieldChange}
          name="shortenText"
          className="p-inputswitch-sm"
        />
      </div>
      <OutputHighlight bgHighlights={bgHighlightList} onAttributeChange={updateAttributeChange} />
      <ValidationOptions
        cellType={CellType.DOUBLE}
        validators={attribute.validationOptions?.validators ?? []}
        onValidationChange={handleValidationChange}
      />
    </>
  );
};

export default LongAndDoubleTypes;
